import { useEffect, useRef, useState } from 'react';

import DocumentRequirementStep from './DocumentRequirementStep/DocumentRequirementStep';
import LoanSummaryRevamp from './LoanSummaryRevamp';
import AgedDebtorReport from './RequirementTypes/AgedDebtorReport';
import BankStatements from './RequirementTypes/BankStatements';
import BusinessPlan from './RequirementTypes/BusinessPlan';
import CompanyAccounts from './RequirementTypes/CompanyAccounts';
import ManagementAccounts from './RequirementTypes/ManagementAccounts';
import VATReturns from './RequirementTypes/VATReturns';
import styles from './TradingHistoryRevamp.module.css';
import {
  useGetApplicationQuery,
  useGetDocuments,
  TGetDocumentsResponse,
  TDocuments,
} from '../../../api/lendingApi/lendingApiQueries';
import { getDurationInMonths } from '../../../helpers/product.helpers';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useNumberFormat } from '../../../hooks/useNumberFormat';
import { useOffersUK } from '../../../hooks/useOffers/useOffersUK';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { TradingHistoryLoader } from '../TradingHistoryLoader';
import { requirementTitles } from './RequirementTitles.helper';
import { LoanSummaryLoader } from '../TradingHistoryUK/LoanSummaryLoader';

const DOCUMENT_REQUIREMENTS: Record<string, React.ElementType> = {
  aged_debtor_report: AgedDebtorReport,
  bank_statement: BankStatements,
  business_plan: BusinessPlan,
  company_accounts: CompanyAccounts,
  management_accounts: ManagementAccounts,
  vat_return: VATReturns,
};

const REQUIREMENT_TYPE_TO_DOCUMENT_TYPE: Record<string, string> = {
  aged_debtor_report: 'aged_debtor_report',
  bank_statement: 'bank statement',
  business_plan: 'business_plan',
  company_accounts: 'business accounts',
  management_accounts: 'management_accounts',
  vat_return: 'vat return',
};

const VALID_DOCUMENT_STATUSES = ['complete', 'pending', 'under_review'];

const TradingHistoryRevamp = () => {
  const { formatCurrency } = useNumberFormat();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [attemptedSteps, setAttemptedSteps] = useState<Set<number>>(new Set());
  const didInitialAutoAdvance = useRef(false);
  const wasRefetching = useRef<boolean | undefined>(undefined);

  const { application } = useGetApplicationQuery();
  const stateKey = useContextStateKey();
  const { documents, isLoading, isRefetching, refetch } =
    useGetDocuments(stateKey);

  const { documentRequirements, isLoadingOffers, offers } = useOffersUK({
    enabled: true,
  });

  const requiredDocumentRequirements =
    documentRequirements?.filter((doc) => !doc.is_optional) || [];

  const findFirstIncompleteStepIndex = () =>
    requiredDocumentRequirements.findIndex(
      (requirement) => !isStepComplete(requirement, documents),
    );

  // Detect when mount refetch completes (isRefetching: true -> false transition)
  useEffect(() => {
    if (
      wasRefetching.current === true &&
      !isRefetching &&
      !didInitialAutoAdvance.current
    ) {
      didInitialAutoAdvance.current = true;
    }
    wasRefetching.current = isRefetching;
  }, [isRefetching]);

  // Refetch documents on mount to ensure fresh data
  useEffect(() => {
    if (stateKey) {
      refetch();
    }
  }, [stateKey, refetch]);

  // Set active step to first incomplete step on initial load only
  useEffect(() => {
    // Wait for mount refetch to complete and ensure we have data
    if (!documents?.data || isLoading || isRefetching) return;

    // Only run once after mount refetch, not after user interaction
    if (didInitialAutoAdvance.current || attemptedSteps.size > 0) return;

    const firstIncompleteIndex = findFirstIncompleteStepIndex();

    if (firstIncompleteIndex !== -1) {
      setActiveStepIndex(firstIncompleteIndex);
    } else if (requiredDocumentRequirements.length > 0) {
      setActiveStepIndex(requiredDocumentRequirements.length - 1);
    }
  }, [
    documents,
    isLoading,
    isRefetching,
    requiredDocumentRequirements,
    attemptedSteps,
  ]);

  const scrollToRequirement = (requirementType: string) => {
    const elementId = `document-requirement-${requirementType}`;
    const element = document.getElementById(elementId);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleContinueToNextStep = (
    currentIndex: number,
    requirement: (typeof requiredDocumentRequirements)[number],
  ) => {
    setAttemptedSteps((prev) => new Set(prev).add(currentIndex));

    if (!isStepComplete(requirement, documents)) {
      scrollToRequirement(requirement.type);
      return;
    }

    const isNotLastStep =
      currentIndex < requiredDocumentRequirements.length - 1;
    if (isNotLastStep) {
      setActiveStepIndex(currentIndex + 1);
    }
  };

  const isStepComplete = (
    requirement: (typeof requiredDocumentRequirements)[number],
    currentDocuments: TGetDocumentsResponse | undefined,
  ) => {
    if (!currentDocuments?.data) return false;

    const documentType = REQUIREMENT_TYPE_TO_DOCUMENT_TYPE[requirement.type];
    if (!documentType) return false;

    const matchingDocuments = currentDocuments.data.filter(
      (doc: TDocuments) => doc.attributes.document_type === documentType,
    );

    return matchingDocuments.some((doc: TDocuments) =>
      VALID_DOCUMENT_STATUSES.includes(doc.attributes.status),
    );
  };

  // Calculate display amount and loan details from offers
  const requestedAmount = application?.requested_products?.[0]?.amount;
  const suggestedOffer = offers?.find((offer) => offer.is_suggested);
  const estimatedAmount = suggestedOffer?.max_amount;
  const maxTermLength = getDurationInMonths(suggestedOffer?.duration);

  const isEstimatedAmountSmaller =
    Number(estimatedAmount) < Number(requestedAmount);
  const displayAmount = isEstimatedAmountSmaller
    ? requestedAmount
    : estimatedAmount;

  if (isLoadingOffers || isLoading) {
    return (
      <div className={styles.container}>
        <ProgressBar />
        <LoanSummaryLoader />
        <TradingHistoryLoader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ProgressBar />
      <div className={styles.content}>
        <LoanSummaryRevamp
          displayAmount={displayAmount}
          maxTermLength={maxTermLength}
          isEstimatedAmountSmaller={isEstimatedAmountSmaller}
        />
        <h1 className={styles.heading}>
          You’re eligible for up to{' '}
          <span className={styles.amount}>
            {formatCurrency(displayAmount)}*
          </span>
        </h1>
        <p className={styles.subheading}>
          We just need a little bit more information to be able to make the most
          accurate assessment.
        </p>
        {requiredDocumentRequirements.length > 0 && (
          <div className={styles.steps}>
            {requiredDocumentRequirements.map((requirement, index) => {
              const RequirementContent =
                DOCUMENT_REQUIREMENTS[requirement.type];
              if (!RequirementContent) return null;

              const title = requirementTitles(
                requirement.type,
                requirement.quantifier?.value,
              );
              if (!title) return null;

              const isActive = index === activeStepIndex;
              const isComplete = isStepComplete(requirement, documents);
              const isError = attemptedSteps.has(index) && !isComplete;

              return (
                <DocumentRequirementStep
                  key={requirement.type}
                  stepNumber={index + 1}
                  title={title}
                  isComplete={isComplete}
                  isActive={isActive}
                  isError={isError}
                  onContinue={() =>
                    handleContinueToNextStep(index, requirement)
                  }
                >
                  {requirement.type === 'bank_statement' ? (
                    <RequirementContent
                      resolutionMethods={requirement.resolution_methods}
                    />
                  ) : (
                    <RequirementContent />
                  )}
                </DocumentRequirementStep>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingHistoryRevamp;
